/**
 * Menu Mortar Animation — cyan shooting stars fall from top corners into void.
 * On void contact: in-game spawnVoidProjectileConsume VFX.
 * Chance to spawn magenta void retaliation orbs (max 3, no recursion).
 */

import { Container, Graphics } from 'pixi.js';
import { WORLD, Z, VOID, VOID_CONSUME } from '../../constants';
import { MORTAR } from '../../weapons';
import type { GameState } from '../../types';
import type { MenuMortarAnim, MenuAnimDeps } from './types';
import { MENU_MORTAR } from './types';

export class MenuMortarAnimation {
    anim: MenuMortarAnim;
    private worldContainer: Container;

    constructor(worldContainer: Container) {
        this.worldContainer = worldContainer;
        this.anim = {
            mortars: [],
            idleTimer: 2 + Math.random() * 4,
            activeVoidShots: 0,
        };
    }

    update(dt: number, state: GameState, deps: MenuAnimDeps): void {
        const anim = this.anim;

        // --- Spawn timer ---
        anim.idleTimer -= dt;
        if (anim.idleTimer <= 0) {
            // Spawn from anywhere along the top edge with varied trajectories
            const style = Math.random();
            let spawnX: number, spawnY: number, vx: number, vy: number;

            if (style < 0.3) {
                // Style A: Steep diagonal from corners (classic shooting star)
                const fromRight = Math.random() < 0.5;
                spawnX = fromRight
                    ? WORLD.VIRTUAL_WIDTH - 30 - Math.random() * 80
                    : 30 + Math.random() * 80;
                spawnY = -20 - Math.random() * 40;
                const targetX = WORLD.VIRTUAL_WIDTH / 2 + (Math.random() - 0.5) * 300;
                vx = (targetX - spawnX) * (0.35 + Math.random() * 0.15);
                vy = 150 + Math.random() * 200;
            } else if (style < 0.55) {
                // Style B: Wide arc from left/right third
                const fromRight = Math.random() < 0.5;
                spawnX = fromRight
                    ? WORLD.VIRTUAL_WIDTH * (0.6 + Math.random() * 0.35)
                    : WORLD.VIRTUAL_WIDTH * (0.05 + Math.random() * 0.35);
                spawnY = -30 - Math.random() * 50;
                // Strong cross-screen horizontal
                vx = (fromRight ? -1 : 1) * (200 + Math.random() * 400);
                vy = 80 + Math.random() * 180;
            } else if (style < 0.8) {
                // Style C: High mortar drop from center region — nearly vertical
                spawnX = WORLD.VIRTUAL_WIDTH * (0.2 + Math.random() * 0.6);
                spawnY = -40 - Math.random() * 60;
                vx = (Math.random() - 0.5) * 200;
                vy = 200 + Math.random() * 300;
            } else {
                // Style D: Fast streaker — spawns off-edge, slashes across screen
                const fromRight = Math.random() < 0.5;
                spawnX = fromRight ? WORLD.VIRTUAL_WIDTH + 20 : -20;
                spawnY = -10 - Math.random() * 80;
                vx = (fromRight ? -1 : 1) * (500 + Math.random() * 400);
                vy = 250 + Math.random() * 250;
            }

            const gfx = new Graphics();
            gfx.zIndex = Z.PROJECTILES;
            this.worldContainer.addChild(gfx);

            anim.mortars.push({
                gfx,
                x: spawnX,
                y: spawnY,
                vx,
                vy,
                trail: [],
                isVoidShot: false,
                willExplode: false,
                pastPeak: false,
            });

            anim.idleTimer = MENU_MORTAR.INTERVAL_MIN +
                Math.random() * (MENU_MORTAR.INTERVAL_MAX - MENU_MORTAR.INTERVAL_MIN);
        }

        // --- Update each mortar ---
        for (let i = anim.mortars.length - 1; i >= 0; i--) {
            const m = anim.mortars[i];
            const color = m.isVoidShot ? MENU_MORTAR.VOID_COLOR : MENU_MORTAR.COLOR;

            // Record trail
            m.trail.push({ x: m.x, y: m.y });
            if (m.trail.length > MENU_MORTAR.TRAIL_MAX) m.trail.shift();

            // Physics
            m.vy += MENU_MORTAR.GRAVITY * dt;
            m.x += m.vx * dt;
            m.y += m.vy * dt;

            // Wall bounce (energetic reflection — preserves shooting star feel)
            if (m.x < WORLD.WORLD_LEFT && m.vx < 0) {
                m.vx = -m.vx * 0.85;
                m.x = WORLD.WORLD_LEFT;
            } else if (m.x > WORLD.WORLD_RIGHT && m.vx > 0) {
                m.vx = -m.vx * 0.85;
                m.x = WORLD.WORLD_RIGHT;
            }

            // --- Void shot peak-of-arc explosion (25% chance, decided at spawn) ---
            if (m.isVoidShot && m.willExplode && !m.pastPeak && m.vy >= 0) {
                m.pastPeak = true;
                // Explode with in-game mortar explosion
                deps.gameParticles.spawnRichExplosion(
                    m.x, m.y, MENU_MORTAR.VOID_COLOR,
                    MORTAR.blastRadius * 0.7, MORTAR.particleScale * 0.6,
                );
                deps.effects.triggerMultiFlash(MENU_MORTAR.VOID_COLOR, VOID.FLASH_ALPHA * 0.2);
                deps.camera.shake(4, { decay: 0.85 });
                deps.screenShake.trigger(3, 0.85);

                // Remove mortar
                this.worldContainer.removeChild(m.gfx);
                m.gfx.destroy();
                anim.activeVoidShots = Math.max(0, anim.activeVoidShots - 1);
                anim.mortars.splice(i, 1);
                continue;
            }

            // --- Render: trail + body (matches in-game renderProjectile) ---
            m.gfx.clear();
            for (let j = 0; j < m.trail.length; j++) {
                const t = j / m.trail.length; // 0→1 (tail→head)
                const tp = m.trail[j];
                m.gfx.circle(tp.x, tp.y, MENU_MORTAR.RADIUS * t * 0.7);
                m.gfx.fill({ color, alpha: t * 0.5 });
            }
            // White body core
            m.gfx.circle(m.x, m.y, MENU_MORTAR.RADIUS);
            m.gfx.fill({ color: 0xffffff });
            // Colored glow halo
            m.gfx.circle(m.x, m.y, MENU_MORTAR.RADIUS * 1.5);
            m.gfx.fill({ color, alpha: 0.4 });

            // --- Void contact → consume ---
            if (m.y >= state.voidY - 3) {
                // Remove mortar gfx
                this.worldContainer.removeChild(m.gfx);
                m.gfx.destroy();

                // In-game void projectile consume VFX (5-phase Sakurai)
                deps.gameParticles.spawnVoidProjectileConsume(m.x, state.voidY);
                deps.voidRenderer.triggerEdgeDistortion(m.x, state.voidY);
                // Toned-down flash for menu
                deps.effects.triggerFlash(0xff00ff, VOID_CONSUME.FLASH_ALPHA * 0.3);
                deps.camera.shake(VOID_CONSUME.CAMERA_SHAKE * 0.15, { decay: 0.85 });
                deps.screenShake.trigger(VOID_CONSUME.SCREEN_SHAKE * 0.15, 0.85);

                // --- Void retaliation chance (only from regular mortars, not void shots) ---
                if (!m.isVoidShot && anim.activeVoidShots < MENU_MORTAR.MAX_VOID_SHOTS) {
                    if (Math.random() < MENU_MORTAR.VOID_CHANCE) {
                        // Schedule a void retaliation orb after a delay
                        const delay = MENU_MORTAR.VOID_DELAY_MIN +
                            Math.random() * (MENU_MORTAR.VOID_DELAY_MAX - MENU_MORTAR.VOID_DELAY_MIN);

                        const consumeX = m.x + (Math.random() - 0.5) * 80;
                        anim.activeVoidShots++;

                        // Foreshadow effect immediately
                        deps.gameParticles.spawnVoidForeshadow(consumeX, state.voidY);
                        deps.voidRenderer.triggerForeshadow(consumeX, state.voidY);

                        // Delayed launch
                        setTimeout(() => {
                            if (anim.activeVoidShots <= 0) return; // already cleaned up

                            // Launch magenta void orb upward from void surface
                            const voidGfx = new Graphics();
                            voidGfx.zIndex = Z.TANKS + 2;
                            this.worldContainer.addChild(voidGfx);

                            anim.mortars.push({
                                gfx: voidGfx,
                                x: consumeX,
                                y: state.voidY - 5,
                                vx: (Math.random() - 0.5) * 300,
                                vy: -500 - Math.random() * 300, // strong upward
                                trail: [],
                                isVoidShot: true,
                                willExplode: Math.random() < 0.40, // 40% chance to explode at peak
                                pastPeak: false,
                            });

                            // Launch VFX
                            deps.gameParticles.spawnVoidLaunchBurst(consumeX, state.voidY);
                            deps.voidRenderer.triggerEdgeDistortion(consumeX, state.voidY);
                            deps.screenShake.trigger(2, 0.85);
                            deps.effects.triggerFlash(0xff00ff, 0.05);
                        }, delay);
                    }
                }

                // If this was a void shot, decrement counter
                if (m.isVoidShot) {
                    anim.activeVoidShots = Math.max(0, anim.activeVoidShots - 1);
                }

                anim.mortars.splice(i, 1);
                continue;
            }

            // Out of bounds (fell way below) — safety cleanup
            if (m.y > WORLD.VIRTUAL_HEIGHT + 200) {
                this.worldContainer.removeChild(m.gfx);
                m.gfx.destroy();
                if (m.isVoidShot) {
                    anim.activeVoidShots = Math.max(0, anim.activeVoidShots - 1);
                }
                anim.mortars.splice(i, 1);
            }
        }
    }

    /** Clean up mortar animation elements (when leaving menu). */
    cleanup(): void {
        const anim = this.anim;
        for (const m of anim.mortars) {
            this.worldContainer.removeChild(m.gfx);
            m.gfx.destroy();
        }
        anim.mortars = [];
        anim.activeVoidShots = 0;
        anim.idleTimer = 2 + Math.random() * 4;
    }
}
